import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type { EffectiveMemberDto, OidcAccessState, OidcClientPickerDto } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { useCurrentOperator } from '@/auth/AuthContext';
import type { TenantWorkspaceLoaderData } from './TenantWorkspace';

/**
 * Tenant "OIDC SSO" tab — per-(member, client) grant matrix.
 *
 * Spec 0058 (saas-auth SASATH-0076) — U3. The old view drove the GLOBAL
 * per-member `oidc_sso_enabled` toggle (`members.setOidcSso` +
 * `TenantMemberDto.oidcSsoEnabled`), both removed by SASATH-0076: OIDC access is
 * now per-(member, client) with the three-state model GRANTED / DISABLED /
 * NOT_GRANTED.
 *
 * No per-member "access across clients" read exists (confirmed via the U1
 * delivery's Architect notes — SASATH-0076 not local, no `/v3/api-docs`). So the
 * matrix is COMPOSED from per-client effective-members reads, BOUNDED by
 * `tenants.oidcClientsForTenant(tenantId)` (the clients whose allow-list includes
 * THIS tenant — a small, bounded set, NOT an all-clients fan-out). For each such
 * client we read `oidcClients.effectiveMembers(clientDbId)` and filter to this
 * tenant; the union forms the members×clients matrix. Each per-client read keeps
 * its display-only resilience (null on error → that column degrades quietly).
 *
 * The per-client grant toggle reuses the U2 pattern (optimistic-via-refetch,
 * inline 400/403, the in-flight key disables that cell's control). The whole-tab
 * SUPER_ADMIN gate is retained (unchanged from the pre-0058 view).
 */

interface MatrixData {
  clients: OidcClientPickerDto[];
  // clientDbId → this tenant's members on that client, or null if the per-client
  // effective-members read failed (that column degrades to "Unavailable").
  byClient: Record<string, EffectiveMemberDto[] | null>;
}

export function TenantOidcSso() {
  const data = useRouteLoaderData('tenant-workspace') as TenantWorkspaceLoaderData;
  const tenant = data.tenant;
  const { t } = useCopy();
  const snack = useSnackbar();
  const operator = useCurrentOperator();
  // undefined = loading; null = the clients-for-tenant picker read failed
  // (display-only resilience → quiet "Not available yet." placeholder).
  const [matrix, setMatrix] = useState<MatrixData | null | undefined>(undefined);
  // Per-cell grant-toggle state, keyed by `${clientDbId}-${userId}`: the in-flight
  // key disables that cell's control; any inline 400/403 renders beneath it.
  const [pending, setPending] = useState<string | null>(null);
  const [cellError, setCellError] = useState<Record<string, string>>({});

  // Read this tenant's members on a single client (bounded element of the compose).
  const loadClient = useCallback(
    async (clientDbId: string): Promise<EffectiveMemberDto[] | null> => {
      const res = await apiAdmin.oidcClients.effectiveMembers(clientDbId);
      return res ? res.members.filter((m) => m.tenantId === tenant.id) : null;
    },
    [tenant.id],
  );

  const load = useCallback(async () => {
    let clients: OidcClientPickerDto[];
    try {
      clients = await apiAdmin.tenants.oidcClientsForTenant(tenant.id);
    } catch {
      setMatrix(null);
      return;
    }
    const entries = await Promise.all(
      clients.map(async (c) => [c.clientDbId, await loadClient(c.clientDbId)] as const),
    );
    setMatrix({ clients, byClient: Object.fromEntries(entries) });
  }, [tenant.id, loadClient]);

  useEffect(() => {
    if (!operator?.isSuperAdmin) return;
    void load();
  }, [load, operator]);

  if (!operator?.isSuperAdmin) {
    return <Alert severity="warning">{t('errors.superAdminRequired', 'Requires Super admin.')}</Alert>;
  }

  // Per-client grant toggle. NOT_GRANTED/DISABLED → Grant (enabled:true); GRANTED →
  // Disable (enabled:false). The toggle returns no body (U1 `void` contract), so on
  // success we refetch only the affected client's effective-members read.
  //
  // SASADM-0076 — the "Grant does nothing" fix: EVERY failure is now surfaced (a
  // snackbar always fires; an ApiError additionally keeps its inline cell note), so
  // a network/timeout/non-ApiError rejection is never silently swallowed. And when
  // the post-write refetch degrades to null (effectiveMembers swallowed an error),
  // we warn that the change was submitted but the view couldn't refresh rather than
  // letting the just-acted cell silently drop to "Unavailable".
  async function setAccess(client: OidcClientPickerDto, m: EffectiveMemberDto, enabled: boolean) {
    const key = `${client.clientDbId}-${m.userId}`;
    setCellError((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setPending(key);
    try {
      await apiAdmin.members.setOidcAccess(tenant.id, m.userId, client.clientDbId, enabled);
      const members = await loadClient(client.clientDbId);
      setMatrix((prev) =>
        prev ? { ...prev, byClient: { ...prev.byClient, [client.clientDbId]: members } } : prev,
      );
      if (members === null) {
        // The write succeeded but this client's effective-members refetch failed;
        // the optimistic patch above kept the old view, so tell the operator the
        // change landed but the grid couldn't refresh.
        snack.warning(
          t(
            'tenantOidcSso.refreshFailed',
            'Change submitted, but the access grid could not be refreshed — reload to confirm.',
          ),
        );
      }
    } catch (err) {
      const action = enabled
        ? t('tenantOidcSso.grantAction', 'grant')
        : t('tenantOidcSso.disableAction', 'disable');
      if (err instanceof ApiError) {
        setCellError((prev) => ({ ...prev, [key]: t(`errors.codes.${err.code}`, err.code) }));
      }
      snack.error(
        t('tenantOidcSso.grantFailed', "Couldn't {{action}} {{member}}'s access to {{client}} — please retry.", {
          action,
          member: m.email,
          client: client.clientName,
        }),
      );
    } finally {
      setPending(null);
    }
  }

  function accessLabel(state: OidcAccessState) {
    if (state === 'GRANTED') return t('tenantOidcSso.accessGranted', 'Granted');
    if (state === 'DISABLED') return t('tenantOidcSso.accessDisabled', 'Disabled');
    return t('tenantOidcSso.accessNotGranted', 'Not granted');
  }

  const heading = (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h2" component="h2" gutterBottom>
        {t('tenantOidcSso.title', 'Connected Apps (OIDC)')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t(
          'tenantOidcSso.intro',
          "Which registered apps (OIDC clients) this tenant's members can sign into using their Journix identity. Each row is a member; each column is an app that allow-lists this tenant.",
        )}
      </Typography>
    </Box>
  );

  if (matrix === undefined) {
    return (
      <Box>
        {heading}
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('common.loading', 'Loading')}
        </Typography>
      </Box>
    );
  }

  if (matrix === null) {
    return (
      <Box>
        {heading}
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('tenantOidcSso.unavailable', 'Not available yet.')}
        </Typography>
      </Box>
    );
  }

  const { clients, byClient } = matrix;

  if (clients.length === 0) {
    return (
      <Box>
        {heading}
        <Alert severity="info">
          {t('tenantOidcSso.noClients', "This tenant isn't allow-listed on any OIDC client.")}
        </Alert>
      </Box>
    );
  }

  // Compose the member set from the union of the per-client reads (filtered to this
  // tenant) — every client allow-listing the tenant returns all its memberships, so
  // the union is the tenant's member roster with each member's per-client state.
  const memberById = new Map<number, EffectiveMemberDto>();
  for (const c of clients) {
    const members = byClient[c.clientDbId];
    if (!members) continue;
    for (const m of members) {
      if (!memberById.has(m.userId)) memberById.set(m.userId, m);
    }
  }
  const members = [...memberById.values()].sort((a, b) => a.email.localeCompare(b.email));
  const allReadsFailed = clients.every((c) => byClient[c.clientDbId] == null);

  function stateFor(clientDbId: string, userId: number): OidcAccessState | null {
    const list = byClient[clientDbId];
    if (!list) return null; // the per-client read failed → column unavailable
    const found = list.find((m) => m.userId === userId);
    return found ? found.accessState : 'NOT_GRANTED';
  }

  return (
    <Box>
      {heading}
      {allReadsFailed ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('tenantOidcSso.unavailable', 'Not available yet.')}
        </Typography>
      ) : members.length === 0 ? (
        <Alert severity="info">{t('tenantOidcSso.empty', 'No members yet.')}</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('tenantOidcSso.member', 'Member')}</TableCell>
                {clients.map((c) => (
                  <TableCell key={c.clientDbId}>{c.clientName}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.userId}>
                  <TableCell>
                    <Typography variant="body2">{member.email}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {t(`members.roles.${member.role}`, member.role)}
                    </Typography>
                  </TableCell>
                  {clients.map((client) => {
                    const state = stateFor(client.clientDbId, member.userId);
                    const key = `${client.clientDbId}-${member.userId}`;
                    if (state === null) {
                      return (
                        <TableCell key={client.clientDbId}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {t('tenantOidcSso.columnUnavailable', 'Unavailable')}
                          </Typography>
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={client.clientDbId}>
                        <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                          {/* The three states render DISTINCTLY — GRANTED /
                              DISABLED / NOT_GRANTED are never collapsed. */}
                          <Typography
                            variant="caption"
                            sx={{ color: state === 'GRANTED' ? 'success.main' : 'text.secondary' }}
                          >
                            {accessLabel(state)}
                          </Typography>
                          {state === 'GRANTED' ? (
                            <Button
                              size="small"
                              color="error"
                              disabled={pending === key}
                              onClick={() => void setAccess(client, member, false)}
                            >
                              {t('tenantOidcSso.disable', 'Disable')}
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              disabled={pending === key}
                              onClick={() => void setAccess(client, member, true)}
                            >
                              {t('tenantOidcSso.grant', 'Grant')}
                            </Button>
                          )}
                          {cellError[key] ? (
                            <Typography variant="caption" sx={{ color: 'error.main' }}>
                              {cellError[key]}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
